import { useEffect, useMemo, useState } from "react";
import { parseFormTemplate } from "./formTemplateParser";

/**
 * The answers to a form's {{input}}/{{textarea}} fields, held positionally, and their
 * "every field is required" check. Shared by the reservation dialog and the page a client
 * opens from an emailed form. The answers are wiped whenever `resetKey` or the template
 * changes, since the dialog stays mounted between openings.
 */
export const usePreReservationFormAnswers = (
  templateText: string,
  resetKey?: unknown,
) => {
  const segments = useMemo(
    () => parseFormTemplate(templateText),
    [templateText],
  );

  const inputCount = useMemo(
    () =>
      segments.filter((s) => s.type === "input" || s.type === "textarea")
        .length,
    [segments],
  );

  const [answers, setAnswers] = useState<string[]>([]);
  const [attempted, setAttempted] = useState(false);

  useEffect(() => {
    setAnswers([]);
    setAttempted(false);
  }, [resetKey, templateText]);

  const setAnswer = (index: number, value: string) => {
    setAnswers((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const isFilled = (index?: number) =>
    index === undefined ? false : (answers[index] ?? "").trim().length > 0;

  const allFilled = Array.from({ length: inputCount }).every((_, i) =>
    isFilled(i),
  );

  /** Empty fields are marked only once the user has tried to send the form. */
  const isInvalid = (index?: number) => attempted && !isFilled(index);

  /**
   * The answers in field order when every field is filled; otherwise marks the empty
   * fields and returns null.
   */
  const collectAnswers = (): string[] | null => {
    if (!allFilled) {
      setAttempted(true);
      return null;
    }

    return Array.from({ length: inputCount }).map((_, i) => answers[i] ?? "");
  };

  return {
    segments,
    answers,
    setAnswer,
    isInvalid,
    showMissingError: attempted && !allFilled,
    collectAnswers,
  };
};
